import isEqual from 'lodash-es/isEqual'
import React, { type ComponentProps } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import type { StatusTagDropdown } from '@components/composed'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { Toasts } from '@fourthwall/components'
import type { CollectionsApi } from '@fourthwall/services/dist/CollectionsApiClient'
import { useOffersByIdsQuery } from '@hooks/useOffersByIdsQuery'
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop'
import { reverse, routing } from '@utils/routing'
import { isTimeFrameFrom, isTimeFrameRange, isTimeFrameTo } from '@utils/timeframe'

import { CollectionView } from './CollectionView'
import { useAvailabilityMutation } from './hooks/useAvailabilityMutation'
import { useSortingStrategyMutation } from './hooks/useSortingStrategyMutation'
import { useStatusMutation } from './hooks/useStatusMutation'
import {
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
  useUpdateCollectionSlugMutation,
} from './mutations'
import { useCollection, useCollectionOffers } from './queries'
import type { FormValues } from './types'
import {
  getCollectionTimeFrame,
  getCreateCollectionPayload,
  getUpdateCollectionDetailsPayload,
  sleep,
} from './utils'

type Offers = NonNullable<ReturnType<typeof useOffersByIdsQuery>['offersByIdsQuery']['data']>

const CollectionContainer = () => {
  const { hasPermission } = useDashboardPermissions()
  const navigate = useNavigate()
  const { collectionId = '' } = useParams<'collectionId'>()
  const { currentShop } = useCurrentShop()
  const isCreating = !collectionId
  const { statusMutation } = useStatusMutation()
  const { availabilityMutation } = useAvailabilityMutation()
  const { updateSortingStrategyMutation } = useSortingStrategyMutation()
  const { collectionQuery, invalidateCollectionQuery } = useCollection([{ collectionId }], {
    enabled: !!collectionId,
    keepPreviousData: false,
  })
  const { collectionOffersQuery, invalidateCollectionOffersQuery } = useCollectionOffers(
    [{ collectionId }],
    { enabled: !!collectionId },
  )
  const [additionalOffers, setAdditionalOffers] = React.useState<string[]>([])
  const { offersByIdsQuery } = useOffersByIdsQuery([additionalOffers], {
    enabled: !!additionalOffers.length,
  })
  const { createCollectionMutation } = useCreateCollectionMutation()
  const { updateCollectionMutation } = useUpdateCollectionMutation()
  const { updateCollectionSlugMutation } = useUpdateCollectionSlugMutation()

  const invalidateQueries = async () => {
    await sleep(1_000) // this gives time for the backend to return updated statuses

    await invalidateCollectionQuery()
    await invalidateCollectionOffersQuery()
  }

  const handleSubmit = async (values: FormValues, withStatusUpdate: boolean) => {
    if (isCreating) {
      const createdCollection = await createCollectionMutation.mutateAsync(
        [getCreateCollectionPayload(values)],
        {
          onSuccess: (data) => {
            const to = reverse(routing.products.collections.collection.self, {
              collectionId: data.id,
            })

            navigate(to)
          },
        },
      )

      if (values.sortingStrategy !== 'MANUAL' && createdCollection) {
        await updateSortingStrategyMutation.mutateAsync([
          { collectionId: createdCollection.id },
          { sortingStrategy: values.sortingStrategy },
        ])
      }

      return
    }

    await updateCollectionMutation.mutateAsync(
      [{ collectionId }, getUpdateCollectionDetailsPayload(values, withStatusUpdate)],
      {
        onSuccess: () => {
          invalidateQueries()
        },
      },
    )

    if (values.isSoldOut !== !collectionQuery.data?.state.available) {
      await availabilityMutation.mutateAsync(
        [{ collectionId }, { available: !values.isSoldOut, shouldUpdateOffersStatus: true }],
        { onSuccess: invalidateQueries },
      )
    }

    const newTimeFrame = getCollectionTimeFrame(
      values.isTimeLimited,
      values.startDate,
      values.endDate,
    )

    if (!isEqual(newTimeFrame, collectionQuery.data?.timeFrame)) {
      await statusMutation.mutateAsync(
        { collectionId, payload: { newTimeFrame, shouldUpdateOffersStatus: withStatusUpdate } },
        { onSuccess: invalidateQueries },
      )
    }

    if (values.sortingStrategy !== collectionQuery.data?.sortingStrategy) {
      await updateSortingStrategyMutation.mutateAsync(
        [{ collectionId }, { sortingStrategy: values.sortingStrategy }],
        { onSuccess: invalidateQueries },
      )
    }
  }

  const handleStatusSubmit = async (
    {
      status,
      startDate,
      endDate,
      setEndDate,
    }: Parameters<ComponentProps<typeof StatusTagDropdown>['onSubmit']>[0],
    withStatusUpdate: boolean,
  ) => {
    const getPayload = (): CollectionsApi.UpdateCollectionState.RequestBody => {
      if (status === 'SCHEDULE') {
        if (startDate && endDate && setEndDate) {
          return {
            newTimeFrame: {
              '@type': 'TIME_FRAME_RANGE',
              from: startDate.toISOString(),
              to: endDate.toISOString(),
            },
            shouldUpdateOffersStatus: false,
          }
        }

        if (startDate && !setEndDate) {
          return {
            newTimeFrame: { '@type': 'TIME_FRAME_FROM', from: startDate.toISOString() },
            shouldUpdateOffersStatus: withStatusUpdate,
          }
        }

        if (!startDate && endDate && setEndDate) {
          return {
            newTimeFrame: { '@type': 'TIME_FRAME_TO', to: endDate.toISOString() },
            shouldUpdateOffersStatus: withStatusUpdate,
          }
        }
      }

      return {
        newState: status !== 'SCHEDULE' ? status : undefined,
        newTimeFrame: { '@type': 'TIME_FRAME_NONE' },
        shouldUpdateOffersStatus: withStatusUpdate,
      }
    }

    await statusMutation.mutateAsync(
      { collectionId, payload: getPayload() },
      {
        onSuccess: invalidateQueries,
        onError: () => {
          Toasts.notify("Couldn't update the status.", { type: 'error' })
        },
      },
    )
  }

  const handleCollectionDelete = async () => {
    await statusMutation.mutateAsync(
      {
        collectionId,
        payload: {
          newTimeFrame: { '@type': 'TIME_FRAME_NONE' },
          newState: 'ARCHIVED',
          shouldUpdateOffersStatus: false,
        },
      },
      {
        onSuccess: (data) => {
          navigate(reverse(routing.products.collections.self))

          Toasts.notify(`Collection "${data.name}" successfully deleted.`, { type: 'info' })
        },
      },
    )
  }

  const handleSlugChange = (newSlug: string) => {
    updateCollectionSlugMutation.mutate([{ collectionId }, { newSlug }], {
      onSuccess: invalidateCollectionQuery,
    })
  }

  const getInitialValues = (): FormValues => {
    if (isCreating || !collectionQuery.isSuccess) {
      return {
        name: '',
        isTimeLimited: false,
        isSoldOut: false,
        sortingStrategy: 'MANUAL',
        selectedProducts: [],
        startDate: undefined,
        endDate: undefined,
      }
    }

    const { name, timeFrame: tf, state, sortingStrategy } = collectionQuery.data

    const startDate = isTimeFrameFrom(tf) || isTimeFrameRange(tf) ? new Date(tf.from) : undefined
    const endDate = isTimeFrameTo(tf) || isTimeFrameRange(tf) ? new Date(tf.to) : undefined
    const isTimeLimited = !!startDate || !!endDate

    return {
      name,
      isTimeLimited,
      isSoldOut: !state.available,
      sortingStrategy: sortingStrategy || 'MANUAL',
      selectedProducts: collectionQuery.data!.offerIds,
      startDate,
      endDate,
    }
  }

  const offers: Offers = (() => {
    const formAdditionalOffers = offersByIdsQuery.data || {}
    const collectionOffers = collectionOffersQuery.data?.offers.reduce(
      (previous, current) => ({ ...previous, [current.id]: current }),
      {},
    )

    // keep collectionOffers second as it's more likely a valid and up to date response
    return { ...formAdditionalOffers, ...collectionOffers }
  })()

  const hasDropProducts = Object.values(offers).some((offer) => !!offer.drop)

  const setSelectedOfferIds = (ids: string[]) => {
    const nonCollectionOfferIds = ids.filter((id) => !collectionQuery.data?.offerIds.includes(id))

    setAdditionalOffers(nonCollectionOfferIds)
  }

  return (
    <CollectionView
      initialValues={getInitialValues()}
      collection={collectionQuery.data}
      offers={offers}
      shopBaseUri={currentShop?.baseUri}
      isCreating={isCreating}
      isFetching={
        collectionQuery.isInitialLoading ||
        collectionOffersQuery.isInitialLoading ||
        offersByIdsQuery.isInitialLoading
      }
      isSubmitting={
        createCollectionMutation.isLoading ||
        updateCollectionMutation.isLoading ||
        availabilityMutation.isLoading ||
        statusMutation.isLoading ||
        updateSortingStrategyMutation.isLoading ||
        updateCollectionSlugMutation.isLoading
      }
      isError={collectionQuery.isError || collectionOffersQuery.isError || offersByIdsQuery.isError}
      isEditable={hasPermission('collections.edit')}
      hasDropProducts={hasDropProducts}
      totalSold={collectionOffersQuery.isSuccess ? collectionOffersQuery.data.sold : undefined}
      setSelectedOfferIds={setSelectedOfferIds}
      onSubmit={handleSubmit}
      onStatusSubmit={handleStatusSubmit}
      onSlugChange={handleSlugChange}
      onCollectionDelete={handleCollectionDelete}
    />
  )
}

export default CollectionContainer
